import { useEffect, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client/react";
import type { DocumentNode } from "graphql";

type EventsPageResponse<TApiItem> = {
  listEventsPage: {
    items: TApiItem[];
    nextToken: string | null;
  };
};

type UseServerEventsPageOptions<TApiItem, TMapped> = {
  /** Workflow-specific list query defining the fields returned for each row. */
  query: DocumentNode;
  /** Organization scope for the query. The hook idles until this is set. */
  organizationId: string | null | undefined;
  /** Number of rows fetched per server page. */
  pageSize: number;
  /** Raw search text; sent to the server as `q` (debounced). */
  search: string;
  /** Additional static query variables (e.g. organizerUserId, state). */
  variables?: Record<string, unknown>;
  /** When false, the hook clears results and skips fetching. */
  enabled?: boolean;
  /** Maps an API row to the shape the page renders. */
  mapItem: (item: TApiItem) => TMapped;
};

type UseServerEventsPageResult<TMapped> = {
  items: TMapped[];
  page: number;
  /** Highest page number reachable so far (grows as the user pages forward). */
  totalPages: number;
  isLoading: boolean;
  error: Error | null;
  retry: () => void;
  setPage: (page: number) => void;
};

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Server-side, one-page-at-a-time pagination over `listEventsPage`.
 *
 * The backend exposes offset pagination behind an opaque `nextToken` and does
 * not return a total count, so page numbers are discovered incrementally: each
 * fetched page yields the token for the next page, and `totalPages` grows as the
 * user advances. Search is delegated to the server via `q` (debounced). This
 * replaces the previous "load every page then slice locally" hybrid.
 */
export function useServerEventsPage<TApiItem, TMapped>(
  options: UseServerEventsPageOptions<TApiItem, TMapped>,
): UseServerEventsPageResult<TMapped> {
  const {
    query,
    organizationId,
    pageSize,
    search,
    variables,
    enabled = true,
  } = options;
  const apolloClient = useApolloClient();

  const [page, setPage] = useState(1);
  const [items, setItems] = useState<TMapped[]>([]);
  const [maxKnownPage, setMaxKnownPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [requestVersion, setRequestVersion] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState(search.trim());

  // Token used to fetch each 1-based page. Page 1 needs no token. A page is only
  // ever rendered as reachable once we have stored its token, so reachable pages
  // always resolve here.
  const pageTokensRef = useRef<Map<number, string | undefined>>(
    new Map([[1, undefined]]),
  );
  const mapItemRef = useRef(options.mapItem);
  mapItemRef.current = options.mapItem;
  const variablesRef = useRef(variables);
  variablesRef.current = variables;

  // Debounce the search text so typing does not fire a request per keystroke.
  useEffect(() => {
    const trimmed = search.trim();
    const timer = setTimeout(() => {
      setDebouncedSearch(trimmed);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [search]);

  // A change to any of these inputs invalidates the discovered page tokens, so
  // the query key drives a reset back to page 1.
  const queryKey = `${organizationId ?? ""}|${debouncedSearch}|${JSON.stringify(
    variables ?? {},
  )}`;
  const prevQueryKeyRef = useRef(queryKey);

  useEffect(() => {
    if (!enabled || !organizationId) {
      setItems([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    let pageToFetch = page;

    if (prevQueryKeyRef.current !== queryKey) {
      prevQueryKeyRef.current = queryKey;
      pageTokensRef.current = new Map([[1, undefined]]);
      setMaxKnownPage(1);

      if (page !== 1) {
        setPage(1); // re-runs this effect with page === 1
        return;
      }

      pageToFetch = 1;
    }

    const nextToken = pageTokensRef.current.get(pageToFetch);
    let isCancelled = false;

    setIsLoading(true);
    setError(null);

    apolloClient
      .query<EventsPageResponse<TApiItem>>({
        query,
        variables: {
          ...variablesRef.current,
          organizationId,
          q: debouncedSearch || undefined,
          limit: pageSize,
          ...(nextToken ? { nextToken } : {}),
        },
        fetchPolicy: "network-only",
      })
      .then(({ data }) => {
        if (isCancelled) {
          return;
        }

        const pageData = data?.listEventsPage;
        setItems((pageData?.items ?? []).map(mapItemRef.current));

        const followingToken = pageData?.nextToken ?? null;
        if (followingToken) {
          pageTokensRef.current.set(pageToFetch + 1, followingToken);
          setMaxKnownPage((current) => Math.max(current, pageToFetch + 1));
        } else {
          // This is the last page per the latest server response. Clamp the
          // discovered range down and discard tokens for any pages beyond it so
          // a shrunken result set can't keep exposing stale page numbers.
          setMaxKnownPage(pageToFetch);
          for (const knownPage of [...pageTokensRef.current.keys()]) {
            if (knownPage > pageToFetch) {
              pageTokensRef.current.delete(knownPage);
            }
          }
        }
      })
      .catch((requestError: unknown) => {
        if (!isCancelled) {
          setError(
            requestError instanceof Error
              ? requestError
              : new Error("Unable to load events"),
          );
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [
    apolloClient,
    query,
    organizationId,
    queryKey,
    page,
    pageSize,
    enabled,
    debouncedSearch,
    requestVersion,
  ]);

  return {
    items,
    page: Math.min(page, maxKnownPage),
    totalPages: maxKnownPage,
    isLoading,
    error,
    retry: () => setRequestVersion((current) => current + 1),
    setPage,
  };
}
